import { useCallback, useMemo, useState, type ReactElement } from 'react'
import {
  isDefined,
  isDeleted,
  isEmpty,
  isNotDefined,
  isNotEmpty,
  isTeamId,
  pluralize,
  searchIncludes,
  type CompanyId,
  type TeamData,
  type TeamId,
} from '@terros/common'
import { useLocalStorage } from '@mantine/hooks'
import { CloseButton, Combobox, Group, Input, Pill, PillsInput, useCombobox, type InputBaseProps } from '@mantine/core'
import { useGroupedTeamOptions, useStorageKey } from '@hooks'
import { SizePrimitives, useProfile, useTeams, useTranslation } from '@front/model'
import { DisplaySelectOption } from '../DisplayOption'
import { ArchivedResultsMenu } from '../ArchivedResultsMenu'
import { type TeamInlineSelectExtraOption } from './TeamInlineSelect'
import { useTeamComboboxOptions } from './TeamComboboxOptions'

type TeamMultiInlineSelectProps = {
  teamFilter?: (team: TeamData) => boolean
  companyId?: CompanyId
  value: TeamId[]
  onChange: (value: TeamId[]) => void
  placeholder?: string
  compact?: boolean
  allowEmpty?: boolean
  emptyOptionLabel?: string
  extraOptions?: TeamInlineSelectExtraOption[]
  allowArchivedToggle?: boolean
} & InputBaseProps

export const TeamMultiInlineSelect = ({
  teamFilter,
  companyId,
  value,
  onChange,
  placeholder = 'Select teams',
  compact = false,
  allowEmpty = false,
  emptyOptionLabel = 'All teams',
  extraOptions,
  allowArchivedToggle = false,
  ...props
}: TeamMultiInlineSelectProps): ReactElement => {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const { user, company } = useProfile()
  const includeTrashKey = useStorageKey('TeamSelect|includeTrash')
  const [includeArchived, setIncludeArchived] = useLocalStorage({
    key: includeTrashKey,
    defaultValue: false,
  })
  const canIncludeArchived = allowArchivedToggle && includeArchived
  const { data: teams = [] } = useTeams({ companyId: companyId ?? user?.companyId, showArchived: 'all' })
  const availableTeams = useMemo(() => {
    if (!user) return []
    if (isEmpty(teams)) return []
    const valueSet = new Set(value)
    const filter = (team: TeamData): boolean => {
      if (!canIncludeArchived && isDeleted(team) && !valueSet.has(team.teamId)) return false
      if (isNotDefined(teamFilter)) return true
      return teamFilter(team)
    }
    return teams.filter(filter)
  }, [canIncludeArchived, teams, value, teamFilter, user])

  const filteredTeams = useMemo(() => {
    if (isEmpty(query)) return availableTeams
    return availableTeams.filter((team) =>
      searchIncludes([team.name, team.teamId, company?.levelNames?.[team.level]], query)
    )
  }, [availableTeams, query, company?.levelNames])

  const groupedTeamOptions = useGroupedTeamOptions(filteredTeams, company)

  const combobox = useCombobox({
    onDropdownOpen: () => {
      combobox.focusSearchInput()
      setQuery('')
    },
    onDropdownClose: () => {
      combobox.resetSelectedOption()
    },
  })

  const handleSelect = (teamId: string): void => {
    if (teamId === 'empty') {
      onChange([])
      return
    }
    if (!isTeamId(teamId)) return
    if (value.includes(teamId)) {
      if (!allowEmpty && value.length <= 1) return
      onChange(value.filter((id) => id !== teamId))
    } else {
      onChange([...value, teamId])
    }
  }

  const handleRemove = useCallback(
    (teamId: TeamId): void => {
      if (!allowEmpty && value.length <= 1) return
      onChange(value.filter((id) => id !== teamId))
    },
    [allowEmpty, onChange, value]
  )

  const handleClearAll = useCallback((): void => {
    if (!allowEmpty) return
    onChange([])
  }, [allowEmpty, onChange])

  const selectedTeams: { teamId: TeamId; name: string; isDeleted?: boolean }[] = useMemo(() => {
    return value
      .map((teamId) => {
        const team = teams.find((t) => t.teamId === teamId)
        if (team) return team
        const extraOption = extraOptions?.find((option) => option.value === teamId)
        if (extraOption) return { name: extraOption.label, teamId: extraOption.value }
        return { name: teamId, teamId, isDeleted: true }
      })
      .filter(isDefined)
  }, [value, teams, extraOptions])

  const options = useTeamComboboxOptions({
    groupedTeamOptions,
    availableTeams,
    selectedValue: isNotEmpty(value) ? value : undefined,
  })

  const clearButton = useMemo(() => {
    if (isEmpty(value) || !allowEmpty) return
    return (
      <CloseButton
        size='sm'
        onClick={(e) => {
          e.stopPropagation()
          handleClearAll()
        }}
        aria-label='Clear all'
      />
    )
  }, [value, allowEmpty, handleClearAll])

  const rightSection = useMemo(() => {
    if (!allowArchivedToggle) return clearButton ?? <Combobox.Chevron />
    return (
      <Group gap={0} wrap='nowrap'>
        <ArchivedResultsMenu
          includeArchived={includeArchived}
          onChange={setIncludeArchived}
          tooltip={t('Team options')}
        />
        {clearButton ?? <Combobox.Chevron style={{ pointerEvents: 'none' }} />}
      </Group>
    )
  }, [allowArchivedToggle, clearButton, includeArchived, setIncludeArchived, t])

  const displayValue = useMemo(() => {
    if (isEmpty(selectedTeams)) return <Input.Placeholder>{placeholder}</Input.Placeholder>
    if (compact) return `${selectedTeams.length} ${pluralize(selectedTeams.length, 'team', 'teams')}`
    const canRemoveTeam = allowEmpty || selectedTeams.length > 1
    return selectedTeams.map((team) => (
      <Pill
        className={team.isDeleted ? 'line-through' : ''}
        key={team.teamId}
        withRemoveButton={canRemoveTeam}
        onRemove={() => handleRemove(team.teamId)}
      >
        {team.name}
      </Pill>
    ))
  }, [selectedTeams, placeholder, handleRemove, compact, allowEmpty])

  return (
    <Combobox store={combobox} width={350} position='bottom-start' onOptionSubmit={handleSelect}>
      <Combobox.Target>
        <PillsInput
          pointer
          flex={compact ? undefined : 1}
          onClick={() => combobox.toggleDropdown()}
          rightSection={rightSection}
          rightSectionWidth={allowArchivedToggle ? SizePrimitives['16'] : undefined}
          rightSectionPointerEvents={allowArchivedToggle || (isNotEmpty(value) && allowEmpty) ? 'all' : 'none'}
          {...props}
        >
          <Pill.Group>{displayValue}</Pill.Group>
        </PillsInput>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          placeholder='Search teams'
        />
        <Combobox.Options mah={400} style={{ overflowY: 'auto' }}>
          {allowEmpty && (
            <Combobox.Option value='empty'>
              <DisplaySelectOption label={emptyOptionLabel} isSelected={isEmpty(value)} />
            </Combobox.Option>
          )}
          {isEmpty(query) &&
            isNotEmpty(extraOptions) &&
            extraOptions.map((option) => (
              <Combobox.Option key={option.value} value={option.value}>
                <DisplaySelectOption label={option.label} isSelected={value.includes(option.value)} />
              </Combobox.Option>
            ))}
          {options}
          {isEmpty(options) && <Combobox.Empty>No teams found</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
